// Global
import { Field, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import {
  FilterAnd,
  FilterEqual,
  SearchResultsInitialState,
  SearchResultsStoreState,
  WidgetDataType,
  useSearchResults,
  widget,
} from '@sitecore-search/react';
import { CardViewSwitcher, Pagination } from '@sitecore-search/ui';
import Parser from 'html-react-parser';
import { useI18n } from 'next-localization';
import { useEffect, useState, useRef } from 'react';

// Lib
import { DartPages } from 'lib/templates/Project.Dart.model';
import { useTheme } from 'lib/context/ThemeContext';

// Local
import Container from 'components/authorable/Layout/DartContainer/DartContainer';
import Button from 'helpers/Button/Button';

import tailwindVariantProduct, { tailwindMain, tailwindVariantPagination } from './tailwindVariant';

import SVG from 'helpers/SVG/SVG';
import Link from 'next/link';

export type PIDSItemModel = {
  product_name: string;
  pids_link: string;
  pids_valid_from: string;
  pids_valid_to: string;
  sds_link: string;
  sds_valid_from: string;
  sds_valid_to: string;
};

export type ProductModel = {
  id: string;
  name: string;
  description: string;
  producttitle: string;
  source_id?: string;
  url: string;
  pids_id: string;
  parent_name: string;
  parent_id: string;
  product_name: string;
  pids_link: string;
  pids_valid_from: string;
  pids_valid_to: string;
  sds_link: string;
  sds_valid_from: string;
  sds_valid_to: string;
};

type SearchResultsProps = {
  defaultPage?: SearchResultsStoreState['page'];
  defaultItemsPerPage?: SearchResultsStoreState['itemsPerPage'];
  defaultKeyphrase?: SearchResultsStoreState['keyphrase'];
  resultsPerPage?: Field<number>;
  noResult?: Field<string>;
  displayResult?: Field<string>;
  prevLabel?: Field<string>;
  nextLabel?: Field<string>;
  pidsLabel?: Field<string>;
  sdsLabel?: Field<string>;
  pidsValidFromLabel?: Field<string>;
  pidsValidToLabel?: Field<string>;
  sdsValidFromLabel?: Field<string>;
  sdsValidToLabel?: Field<string>;
  itemId?: string;
};

type InitialState = SearchResultsInitialState<'itemsPerPage' | 'keyphrase' | 'page'>;

export type CategoryListPage = DartPages.CategoryListPage;
type SearchSettings = {
  previewSource: string;
  source: string;
};
const defaultCardView = CardViewSwitcher.CARD_VIEW_GRID;

export const PIDSResultsListwithSearchComponent = ({
  defaultPage = 1,
  defaultKeyphrase = '',
  defaultItemsPerPage = 20,
  noResult,
  resultsPerPage,
  pidsLabel,
  sdsLabel,
  pidsValidFromLabel,
  pidsValidToLabel,
  sdsValidFromLabel,
  sdsValidToLabel,
  itemId,
}: SearchResultsProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const searchSettings = sitecoreContext?.searchDetails as SearchSettings;
  const searchSourceIds = process.env.IS_PREVIEW_SITE
    ? searchSettings?.previewSource?.split('|') || []
    : searchSettings?.source?.split('|') || [];

  const filterQuery = new FilterAnd([
    new FilterEqual('pagetype', 'PIDS'),
    new FilterEqual('pids_id', itemId),
  ]);

  const {
    widgetRef,
    actions: { onPageNumberChange },
    state: { page, itemsPerPage },
    queryResult: { isFetching, data: { total_item: totalItems = 0, content: products = [] } = {} },
  } = useSearchResults<ProductModel, InitialState>({
    query: (query) => {
      query
        .getRequest()
        .setSources(searchSourceIds)
        .setSearchFilter(filterQuery)
        .setSearchFacetAll(false);
    },
    state: {
      page: defaultPage,
      itemsPerPage: (resultsPerPage?.value as number) || defaultItemsPerPage,
      keyphrase: defaultKeyphrase,
    },
  });

  const [dir] = useState(defaultCardView);
  const [windowSize, setWindowSize] = useState<number>();
  const [isPreviousButtonDisabled, setPreviousButtonDisabled] = useState<boolean>(false);
  const [isNextButtonDisabled, setNextButtonDisabled] = useState<boolean>(false);
  const { themeName } = useTheme();

  const refSearchResults = useRef<HTMLDivElement>(null);
  const firstCardFocus = useRef<HTMLDivElement>(null);

  const header = typeof window !== 'undefined' ? window.document.getElementById('header') : null;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const scrollToSearchResults = (page: number) => {
    onPageNumberChange({ page });
    const { current } = refSearchResults;
    if (firstCardFocus.current) {
      firstCardFocus.current.tabIndex = 0;
      firstCardFocus.current.focus();
    }
    if (current !== null) {
      setTimeout(() => {
        const currentScrollPosition = window.scrollY || window.pageYOffset;
        const elementTop = current.getBoundingClientRect().top;
        if (header) {
          const newPosition = currentScrollPosition + elementTop - header?.offsetHeight;
          window.scrollTo({ top: newPosition, behavior: 'smooth' });
        }
      }, 300);
    }
  };

  useEffect(() => {
    const initPreviousButton = (): void => {
      if (page === 1) {
        return setPreviousButtonDisabled(true);
      } else if (page !== 1 && totalPages > 1) {
        return setPreviousButtonDisabled(false);
      } else {
        return setPreviousButtonDisabled(true);
      }
    };

    const initNextButton = (): void => {
      if ((page === 1 && totalPages === 1) || (page !== 1 && page === totalPages)) {
        return setNextButtonDisabled(true);
      } else if ((page === 1 && totalPages > 1) || (page > 1 && page !== totalPages)) {
        return setNextButtonDisabled(false);
      } else {
        return setNextButtonDisabled(true);
      }
    };
    initPreviousButton();
    initNextButton();
  }, [page, totalPages]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setWindowSize(window.innerWidth);
    }

    const handleResize = () => {
      setWindowSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [windowSize]);

  const i18n = useI18n();
  let i18n_displayingCountForSearch = i18n.t('DisplayingCountForSearch');
  let i18n_displayingMobileCountForSearch = i18n.t('DisplayingMobileCountForSearch');
  const i18n_next = i18n.t('Next');
  const i18n_previous = i18n.t('Previous');

  const {
    productListContainer,
    productTitle,
    productCard,
    productSection,
    topSection,
    productListLabel,
    productMainLabel,
    productDates,
  } = tailwindVariantProduct({
    /* eslint-disable  @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    brand: themeName as string,
  });

  const {
    base,
    paginationContainer,
    paginationIcon,
    paginationRoot,
    paginationPage,
    paginationPageWrapper,
    paginationDisplayPage,
    paginationGroup,
  } = tailwindVariantPagination();

  const { mainContainer } = tailwindMain();

  const deviceName = windowSize && windowSize >= 996 ? 'Desktop' : 'Mobile';
  const paginationOf = `${itemsPerPage * (page - 1) + 1} - ${
    itemsPerPage * (page - 1) + products.length
  }`;

  const formatDate = (date: string | undefined): string => {
    if (!date) return 'Invalid Date';

    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return 'Invalid Date';

    const day = String(parsedDate.getUTCDate()).padStart(2, '0');
    const month = String(parsedDate.getUTCMonth() + 1).padStart(2, '0'); // Month is 0-based
    const year = parsedDate.getUTCFullYear();

    return `${day}/${month}/${year}`;
  };

  const toalProductPages = totalItems as unknown as string;
  i18n_displayingCountForSearch = i18n_displayingCountForSearch
    .replace('{0}', paginationOf)
    .replace('{1}', toalProductPages);

  i18n_displayingMobileCountForSearch = i18n_displayingMobileCountForSearch
    .replace('{0}', `${page}`)
    .replace('{1}', `${totalPages}`);

  return (
    <Container>
      <div ref={widgetRef}>
        <div className={mainContainer()} ref={refSearchResults}>
          {totalItems > 0 && (
            <>
              <>
                {dir === CardViewSwitcher.CARD_VIEW_GRID && (
                  <div ref={firstCardFocus}>
                    {products?.map((product, index) => (
                      <>
                        {(product?.pids_link || product?.sds_link) && (
                          <>
                            <div
                              className={productCard()}
                              key={index}
                              data-component="widgets/productlistwithsearch/productcard"
                            >
                              <div className={productListContainer()}>
                                <div className={productTitle()}>
                                  {Parser(product?.product_name)}
                                </div>
                                <div className={productListLabel()}>
                                  {/* PIDS - PDF */}
                                  {product?.pids_link ? (
                                    <div className={productSection()}>
                                      {pidsLabel?.value && (
                                        <div className={topSection()}>
                                          {/* pidsLabel */}
                                          <div className={productMainLabel()}>
                                            <Link target="_blank" href={product?.pids_link}>
                                              {pidsLabel?.value}
                                            </Link>
                                          </div>
                                          {/* Date From */}
                                          <div className={productDates()}>
                                            {pidsValidFromLabel?.value}
                                            {': '}
                                            {formatDate(product?.pids_valid_from)}
                                          </div>
                                          {/* Date To */}
                                          {product?.pids_valid_to && (
                                            <div className={productDates()}>
                                              {pidsValidToLabel?.value}
                                              {': '}
                                              {formatDate(product?.pids_valid_to)}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className={productSection()}>
                                      <></>
                                    </div>
                                  )}

                                  {/* SDS - PDF */}
                                  {product?.sds_link ? (
                                    <div className={productSection()}>
                                      {sdsLabel?.value && (
                                        <div className={topSection()}>
                                          {/* sdsLabel */}
                                          <div className={productMainLabel()}>
                                            <Link target="_blank" href={product?.sds_link}>
                                              {sdsLabel?.value}
                                            </Link>
                                          </div>
                                          {/* Date From */}
                                          {product?.sds_valid_from && (
                                            <div className={productDates()}>
                                              {sdsValidFromLabel?.value}
                                              {': '} {formatDate(product?.sds_valid_from)}
                                            </div>
                                          )}
                                          {/* Date To */}
                                          {product?.sds_valid_to && (
                                            <div className={productDates()}>
                                              {sdsValidToLabel?.value}
                                              {': '} {formatDate(product?.sds_valid_to)}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className={productSection()}>
                                      <></>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className={base()}>
                              <SVG svg={`ProductResultSearch/Brand=${themeName}`} />
                            </div>
                          </>
                        )}
                      </>
                    ))}
                  </div>
                )}
              </>
              <div className={paginationContainer()}>
                <Pagination.Root
                  currentPage={page}
                  defaultCurrentPage={1}
                  totalPages={totalPages}
                  onPageChange={(pagination) => {
                    scrollToSearchResults(pagination);
                  }}
                  className={paginationRoot()}
                >
                  <Pagination.PrevPage
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSearchResults(page - 1);
                    }}
                    className={paginationIcon()}
                    tabIndex={-1}
                  >
                    <Button
                      type="text"
                      iconLeft="chevron_left"
                      label={deviceName === 'Desktop' ? i18n_previous : ''}
                      disabled={isPreviousButtonDisabled}
                      tabIndex={isPreviousButtonDisabled ? -1 : 0}
                    />
                  </Pagination.PrevPage>
                  <div className={paginationGroup()}>
                    <Pagination.Pages className={paginationPageWrapper()}>
                      {(pagination) =>
                        Pagination.paginationLayout(pagination, {
                          boundaryCount: 1,
                          siblingCount: 1,
                        }).map(({ page, type }) =>
                          type === 'page' ? (
                            <Pagination.Page
                              key={page}
                              aria-label={`Page ${page}`}
                              page={page as number}
                              onClick={(e) => e.preventDefault()}
                              className={paginationPage({
                                currentPage: page == pagination.currentPage,
                              })}
                            >
                              {page}
                            </Pagination.Page>
                          ) : (
                            <span key={type}>...</span>
                          )
                        )
                      }
                    </Pagination.Pages>
                    {totalItems && (
                      <div
                        className={paginationDisplayPage({
                          isMobile: (windowSize && windowSize <= 996) as boolean,
                        })}
                      >
                        {windowSize && windowSize > 996
                          ? i18n_displayingCountForSearch
                          : i18n_displayingMobileCountForSearch}
                      </div>
                    )}
                    {totalItems && (
                      <div
                        className={paginationDisplayPage({
                          isMobile: (windowSize && windowSize > 996) as boolean,
                        })}
                      >
                        {i18n_displayingCountForSearch}
                      </div>
                    )}
                  </div>
                  <Pagination.NextPage
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSearchResults(page + 1);
                    }}
                    className={paginationIcon()}
                    tabIndex={-1}
                  >
                    {' '}
                    <Button
                      type="text"
                      label={deviceName === 'Desktop' ? i18n_next : ''}
                      iconRight="chevron_right"
                      disabled={isNextButtonDisabled}
                      tabIndex={isNextButtonDisabled ? -1 : 0}
                    />
                  </Pagination.NextPage>
                </Pagination.Root>
              </div>
            </>
          )}
          {totalItems <= 0 && !isFetching && (
            <div>
              <h3>{noResult?.value}</h3>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};
const PIDSResultsListwithSearchWidget = widget(
  PIDSResultsListwithSearchComponent,
  WidgetDataType.SEARCH_RESULTS,
  'content'
);

export default PIDSResultsListwithSearchWidget;
